import React, { useRef } from 'react';

const FileInput = ({
  label,
  required = false,
  error = false,
  helperText,
  accept,
  onChange,
  value = '',
  multiple = false,
  disabled = false,
  wrapperClassName = '',
}) => {
  const inputRef = useRef(null);

  const hasError = !!error;
  const effectiveHelperText = helperText || (typeof error === 'string' && error ? error : null);

  const acceptHint = (() => {
    if (!accept) return null;
    return accept
      .split(',')
      .map((a) => a.trim().replace('image/', '').replace('.', '').toUpperCase())
      .join(', ');
  })();

  return (
    <div className={wrapperClassName}>
      {label && (
        <label className="mb-1 block text-[13px] font-medium text-[#212B36]">
          {label}
          {required && <span className="ml-0.5 text-[#EF4444]">*</span>}
        </label>
      )}

      {/* label wraps the input so any click on the zone reaches the native file picker.
          No programmatic .click() needed — avoids dialog/modal blocking issues. */}
      <label
        className={[
          'flex h-14 w-full items-center gap-3 rounded-lg border-2 border-dashed px-4 transition-colors',
          disabled ? 'pointer-events-none opacity-50 cursor-default' : 'cursor-pointer',
          hasError
            ? 'border-[#EF4444]/60 bg-[#FEF2F2]'
            : 'border-[#d0d5dd] bg-slate-50 hover:border-[#0061B2]/40 hover:bg-slate-100',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={onChange}
          className="sr-only"
        />

        {/* Upload icon */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          className="h-5 w-5 shrink-0 text-[#0061B2]"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>

        {/* Text */}
        <div className="flex-1 overflow-hidden">
          {value ? (
            <span className="block truncate text-sm font-medium text-[#212B36]">{value}</span>
          ) : (
            <>
              <span className="block text-sm text-[#0061B2]">Click to choose file</span>
              {acceptHint && (
                <span className="block text-xs text-[#9aa3ab]">{acceptHint}</span>
              )}
            </>
          )}
        </div>

        {/* Clear button — e.preventDefault() stops the label from re-opening the picker */}
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (inputRef.current) inputRef.current.value = '';
              if (onChange) onChange({ target: { files: [] } });
            }}
            className="shrink-0 text-[#9aa3ab] hover:text-[#EF4444]"
            aria-label="Clear file"
          >
            ×
          </button>
        )}
      </label>

      {effectiveHelperText && (
        <p className={`mt-1 text-xs ${hasError ? 'text-[#EF4444]' : 'text-[#637381]'}`}>
          {effectiveHelperText}
        </p>
      )}
    </div>
  );
};

export default FileInput;
